import { supabase } from '../lib/supabase';

const isSchemaError = (error) => {
  if (!error) return false;

  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') return true;
    if (errorClass === '23') return false;
  }

  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }

  return false;
};

const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);

  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);

  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
};

export const vehicleDeliveryService = {
  async create(deliveryData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      
      const snakeData = toSnakeCase({
        ...deliveryData,
        deliveredBy: user?.id,
        deliveredAt: new Date()?.toISOString()
      });

      const { data, error } = await supabase?.from('vehicle_deliveries')?.insert(snakeData)?.select(`
          *,
          work_order:work_orders(*),
          delivered_by_user:user_profiles!vehicle_deliveries_delivered_by_fkey(id, full_name, email)
        `)?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        throw new Error(error.message);
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to create vehicle delivery:', error);
      throw error;
    }
  },

  async getByWorkOrderId(workOrderId) {
    try {
      const { data, error } = await supabase?.from('vehicle_deliveries')?.select(`
          *,
          work_order:work_orders(*),
          delivered_by_user:user_profiles!vehicle_deliveries_delivered_by_fkey(id, full_name, email)
        `)?.eq('work_order_id', workOrderId)?.maybeSingle();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') return null;
        console.log('Error fetching delivery:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to fetch vehicle delivery:', error);
      throw error;
    }
  },

  async uploadPhoto(file, workOrderId) {
    try {
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${workOrderId}/delivery_${Date.now()}.${fileExt}`;
      const filePath = fileName;

      const { data, error } = await supabase?.storage
        ?.from('work-execution-photos')
        ?.upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('Error uploading photo:', error?.message);
        throw error;
      }

      return data?.path;
    } catch (error) {
      console.error('Failed to upload photo:', error);
      throw error;
    }
  },

  async getPhotoUrl(filePath) {
    try {
      const { data, error } = await supabase?.storage
        ?.from('work-execution-photos')
        ?.createSignedUrl(filePath, 3600);

      if (error) {
        console.error('Error getting photo URL:', error?.message);
        throw error;
      }

      return data?.signedUrl;
    } catch (error) {
      console.error('Failed to get photo URL:', error);
      throw error;
    }
  },

  async deletePhoto(filePath) {
    try {
      const { error } = await supabase?.storage
        ?.from('work-execution-photos')
        ?.remove([filePath]);

      if (error) {
        console.error('Error deleting photo:', error?.message);
        throw error;
      }

      return true;
    } catch (error) {
      console.error('Failed to delete photo:', error);
      throw error;
    }
  }
};